import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../components/layout/Sidebar";
import Navbar from "../../components/layout/Navbar"; // Integrado para consistencia
import { AuthContext } from "../../context/AuthContext";
import { getProcesses } from "../../services/reference.service";
import { createJob } from "../../services/jobs.service";
import { 
  ChevronLeft, 
  Save, 
  Settings, 
  Calendar, 
  FileText, 
  Clock, 
  AlertCircle,
  Loader2
} from "lucide-react";

export default function CreateJob() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [processes, setProcesses] = useState([]);
  const [selectedProcess, setSelectedProcess] = useState("");
  const [observation, setObservation] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadProcesses = async () => {
      setLoading(true);
      const token = localStorage.getItem("token") || "";
      const data = await getProcesses(token);
      setProcesses(data);
      setLoading(false);
    };
    loadProcesses();
  }, []);

  const handleSave = async () => {
    if (!selectedProcess || !startDate) {
      alert("⚠️ Debe seleccionar un proceso y una fecha de inicio.");
      return;
    }

    const token = localStorage.getItem("token") || "";
    const payload = {
      StartDate: new Date(startDate).toISOString(),
      EndDate: endDate ? new Date(endDate).toISOString() : null,
      Observation: observation || null,
      ProcessId: parseInt(selectedProcess),
      UserCreated: user?.dni || "system",
    };

    setSaving(true);
    const result = await createJob(token, payload);
    setSaving(false);

    if (result.success === 1) {
      navigate("/jobs");
    } else {
      alert(`⚠️ Error al crear Job: ${result.message || "Intente nuevamente."}`);
    }
  };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden text-slate-900">
      <Sidebar />

      <main className="flex-1 flex flex-col overflow-hidden">
        <Navbar />

        {/* HEADER DE ACCIÓN */}
        <div className="px-8 py-5 bg-white border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400 hover:text-slate-900"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <div>
              <span className="text-[10px] font-black text-blue-600 uppercase tracking-[0.2em]">Administración</span>
              <h2 className="text-2xl font-black text-slate-900 leading-none">Nueva Orden de Trabajo</h2>
            </div>
          </div>
        </div>

        {/* CONTENIDO DEL FORMULARIO */}
        <div className="flex-1 overflow-y-auto p-6 md:p-10">
          <div className="max-w-3xl mx-auto">
            
            <div className="bg-white rounded-[2.5rem] shadow-xl shadow-slate-200/50 border border-slate-200 overflow-hidden">
              <div className="p-8 md:p-12 space-y-8">
                
                {/* 1. Selección de Proceso */}
                <div className="space-y-4">
                  <label className="flex items-center gap-2 text-xs font-black text-slate-400 uppercase tracking-widest">
                    <Settings className="w-4 h-4 text-blue-500" /> Seleccionar Proceso Maestro
                  </label>
                  {loading ? (
                    <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                      <span className="text-sm text-slate-500 font-medium italic">Sincronizando catálogo de procesos...</span>
                    </div>
                  ) : (
                    <div className="relative group">
                      <select
                        value={selectedProcess}
                        onChange={(e) => setSelectedProcess(e.target.value)}
                        className="w-full bg-slate-50 border-2 border-slate-100 group-hover:border-blue-200 rounded-2xl px-6 py-4 appearance-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all font-bold text-slate-700"
                      >
                        <option value="">— Elegir un proceso operativo —</option>
                        {processes.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.processCode} • {p.processName}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 group-hover:text-blue-500 transition-colors">
                        <ChevronLeft className="-rotate-90 w-5 h-5" />
                      </div>
                    </div>
                  )}
                </div>

                {/* 2. Fechas de Programación */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-slate-50">
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                      <Calendar className="w-3 h-3" /> Fecha Programada Inicio
                    </label>
                    <input
                      type="datetime-local"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-5 py-3 focus:border-blue-500 outline-none transition-all font-medium"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                      <Clock className="w-3 h-3" /> Fecha Estimada Fin
                    </label>
                    <input
                      type="datetime-local"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-5 py-3 focus:border-blue-500 outline-none transition-all font-medium"
                    />
                  </div>
                </div>

                {/* 3. Observaciones */}
                <div className="space-y-2 pt-4">
                  <label className="flex items-center gap-2 text-xs font-black text-slate-400 uppercase tracking-widest">
                    <FileText className="w-4 h-4 text-slate-300" /> Instrucciones Adicionales
                  </label>
                  <textarea
                    value={observation}
                    onChange={(e) => setObservation(e.target.value)}
                    placeholder="Ej: Revisar sellos de seguridad antes de iniciar..."
                    className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-6 py-4 focus:border-blue-500 outline-none transition-all font-medium italic text-sm text-slate-600 min-h-[120px]"
                  />
                </div>

                {/* Banner Informativo */}
                {!selectedProcess && (
                  <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-2xl border border-amber-100">
                    <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700 font-medium leading-relaxed">
                      Selecciona un proceso para que los operarios puedan visualizar las actividades y validaciones obligatorias en sus dispositivos móviles.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER DE ACCIÓN */}
        <footer className="bg-white border-t border-slate-200 px-8 py-5 flex items-center justify-end shadow-[0_-10px_30px_rgba(0,0,0,0.02)]">
          <div className="flex gap-4 w-full md:w-auto">
            <button
              onClick={() => navigate(-1)}
              className="flex-1 md:flex-none px-8 py-3.5 border-2 border-slate-100 rounded-2xl font-bold text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-all active:scale-95"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-12 py-3.5 rounded-2xl font-black text-white transition-all shadow-xl active:scale-95 ${
                saving ? "bg-slate-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 shadow-blue-500/20"
              }`}
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              {saving ? "Creando..." : "Lanzar Orden de Trabajo"}
            </button>
          </div>
        </footer>
      </main>
    </div>
  );
}